/**
 * The avatar menu's stats glance (Member Stats v2, contract Amendment B.2; design "Avatar Menu"
 * boards 2a fresh / 2b median / 2c top) — the pure rules behind `AccountMenuStats`, the one
 * browser read behind them, and the Stats entry's "NEW" flag.
 *
 * The menu is the reader's own surface: every sentence is second person, and nothing here is in a
 * shared-cached HTML (the menu hydrates on open). Figures are DealHunt's — the stats payload's,
 * the member payload's `counts.replies_all` / `badges`, the summary's points table (Constraint
 * f8709b73: never a literal "+15"). The B.1 reading-time rule and the closest-badge pick are the
 * Stats tab's own (`readingTimeState`, `nextBadge` in `./stats`) — one rule per fact.
 *
 * One promise per handle for five minutes (`loadMenuStats`); a failure is forgotten so the next
 * open asks again. Warmed early (Amendment C.3, Guillaume's "early load" 2026-10-11 — overrides
 * 96f85940's "only on menu open, never per page view or on hover intent"): on idle for a
 * remembered hub handle under /community only (`scheduleIdleMenuWarm`), and on the avatar
 * button's hover/focus site-wide — a PUBLIC,
 * edge-cacheable read, never `/me` (which still reads on open only, Constraint a8b375cd). The
 * open then answers from `peekMenuStats` on its first render: no placeholder flash.
 */
import { withSlash } from 'lib/dealhunt/url'
import type { OwnerCommunityMemberStats } from 'lib/dealhunt/types'
import { reviewPointsToday } from './hub'
import { afterLoadIdle } from './prefetch'
import { COMMUNITY_PATH, sentCount, tierLabel, type BadgeCap } from './model'
import {
  fmtCount,
  fmtSeconds,
  dayLabel,
  freshState,
  memberStatsOf,
  monthLabel,
  nextBadge,
  readingTimeState,
  type Voice,
} from './stats'
import type { CommunityBadge, CommunityBadgeId, CommunityStanding } from './types'

type Stats = OwnerCommunityMemberStats
type StatsReview = Stats['reviews'][number]

// ── the read ──────────────────────────────────────────────────────────────────

/**
 * `GET /api/community/members/<handle>/stats` — DealHunt's stats payload as sent, plus the
 * slices of the member payload and the summary the menu's "Up next" needs. `member` / `points`
 * are null when that read failed (the dependent line is then dropped, never guessed).
 */
export interface MenuStatsResponse {
  stats: Stats
  member: { replies_all: number | null; badges: CommunityBadge[] } | null
  points: { review: number | null; reply: number | null; photo_verified: number | null } | null
  caps: Partial<Record<CommunityBadgeId, BadgeCap>>
}

/** The route's path, slashed like every path when the app is (no 308 per open). */
export function menuStatsApiPath(handle: string, trailingSlash: boolean): string {
  return withSlash(
    `/api/community/members/${encodeURIComponent(handle.toLowerCase())}/stats`,
    trailingSlash
  )
}

/** The route's answer, or null when it is not one for `handle` (an error body, an older build). */
export function menuStatsResponseOf(raw: unknown, handle: string): MenuStatsResponse | null {
  if (typeof raw !== 'object' || raw === null) {
    return null
  }
  const r = raw as Partial<MenuStatsResponse>
  const stats = memberStatsOf(r.stats, handle)
  if (!stats) {
    return null
  }
  const member =
    r.member && typeof r.member === 'object' && Array.isArray(r.member.badges)
      ? { replies_all: sentCount(r.member.replies_all), badges: r.member.badges }
      : null
  const points =
    r.points && typeof r.points === 'object'
      ? {
          review: sentCount(r.points.review),
          reply: sentCount(r.points.reply),
          photo_verified: sentCount(r.points.photo_verified),
        }
      : null
  const caps = r.caps && typeof r.caps === 'object' ? r.caps : {}
  return { stats, member, points, caps }
}

/** How long one answer serves every open of the menu. */
export const MENU_STATS_TTL_MS = 5 * 60 * 1000

const cache = new Map<string, { at: number; request: Promise<MenuStatsResponse | null> }>()
/** The same answers once settled (successes only), readable during render. */
const settled = new Map<string, { at: number; value: MenuStatsResponse }>()

async function ask(
  key: string,
  trailingSlash: boolean,
  at: number
): Promise<MenuStatsResponse | null> {
  try {
    const res = await fetch(menuStatsApiPath(key, trailingSlash), {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
    })
    const answer = res.ok ? menuStatsResponseOf(await res.json(), key) : null
    if (!answer) {
      // 503 (`stats_unavailable` before DealHunt's db:push), 404, a blip: the v1 head this time,
      // and the next open asks again.
      cache.delete(key)
    } else {
      settled.set(key, { at, value: answer })
    }
    return answer
  } catch {
    cache.delete(key)
    return null
  }
}

/** One read per handle per five minutes, however often the menu opens. Never rejects. */
export function loadMenuStats(
  handle: string,
  trailingSlash: boolean,
  now: number = Date.now()
): Promise<MenuStatsResponse | null> {
  const key = handle.toLowerCase()
  const known = cache.get(key)
  if (known && now - known.at < MENU_STATS_TTL_MS) {
    return known.request
  }
  const request = ask(key, trailingSlash, now)
  cache.set(key, { at: now, request })
  return request
}

/** The settled, still-fresh answer for `handle`, if any — synchronous, so a menu opened on a
 *  warmed cache renders the glance at once instead of the loading placeholder. */
export function peekMenuStats(handle: string, now: number = Date.now()): MenuStatsResponse | null {
  const hit = settled.get(handle.toLowerCase())
  return hit && now - hit.at < MENU_STATS_TTL_MS ? hit.value : null
}

/** The idle warm runs under /community only (coordinator ruling 2026-10-11): a hard load of an
 *  article page costs no community read — there the glance warms on avatar intent instead. */
export function idleMenuWarmAllowed(path: string): boolean {
  return path === COMMUNITY_PATH || path.startsWith(`${COMMUNITY_PATH}/`)
}

/**
 * Schedule the idle warm of `handle`'s glance; the path is read when the idle callback RUNS (not
 * when scheduled), so a navigation away before idle cancels the read. Returns a cancel.
 */
export function scheduleIdleMenuWarm(
  handle: string,
  trailingSlash: boolean,
  currentPath: () => string,
  whenIdle: (fn: () => void) => () => void = afterLoadIdle
): () => void {
  return whenIdle(() => {
    if (idleMenuWarmAllowed(currentPath())) {
      void loadMenuStats(handle, trailingSlash)
    }
  })
}

/** Test seam: forget every cached answer. */
export function forgetMenuStats(): void {
  cache.clear()
  settled.clear()
}

// ── the Stats entry's "NEW" ───────────────────────────────────────────────────

/** Set once the reader has opened a Stats tab; the menu's "NEW" pill shows until then. */
export const STATS_SEEN_KEY = 'sg_stats_seen'

/** Whether this browser has opened the Stats tab. Storage blocked → seen (no pill that can
 *  never go away). */
export function statsSeen(): boolean {
  try {
    return window.localStorage.getItem(STATS_SEEN_KEY) !== null
  } catch {
    return true
  }
}

export function markStatsSeen(): void {
  try {
    window.localStorage.setItem(STATS_SEEN_KEY, '1')
  } catch {
    // storage blocked: the pill is already hidden for this browser (`statsSeen`)
  }
}

// ── the glance ────────────────────────────────────────────────────────────────

const OWN = (handle: string): Voice => ({ own: true, handle })

/** No counted review: the blue card instead of the grey block. */
export function menuIsFresh(stats: Stats): boolean {
  return stats.reviews.length === 0
}

export interface FreshCard {
  /** "A first owner review here typically reaches about 700 readers in its first week."; null
   *  when DealHunt has no community median. */
  reach: string | null
  /** "+50 pts and Contributor on publish." — the points only while a review can still earn
   *  today, the tier only when those points reach it; null without a figure. */
  award: string | null
}

export function freshCard(
  stats: Stats,
  standing: CommunityStanding | null,
  reviewPoints: number | null
): FreshCard {
  const expected = freshState(stats, OWN(stats.handle))?.expected ?? null
  const pts = reviewPointsToday(standing, reviewPoints)
  let award: string | null = null
  if (pts !== null) {
    const next = standing?.next_tier ?? null
    const tier = next && pts >= next.points_to_go ? ` and ${tierLabel(next.id)}` : ''
    award = `+${pts.toLocaleString('en-US')} pts${tier} on publish.`
  }
  return {
    reach:
      expected !== null
        ? `A first owner review here typically reaches about ${fmtCount(
            expected
          )} readers in its first week.`
        : null,
    award,
  }
}

/** The sparkline's 32px rule (design `days`): the day's share of the busiest day, at least 3px;
 *  a zero day a 2px grey stub; today (the last bar) lighter. */
export const SPARK_MAX_PX = 32

export interface SparkBar {
  day: string
  readers: number
  px: number
  tone: 'zero' | 'today' | 'day'
}

export function sparkBars(days: Stats['readers']['days']): SparkBar[] {
  const values = days.map(d => sentCount(d.readers) ?? 0)
  const max = Math.max(1, ...values)
  return days.map((d, i) => {
    const v = values[i]
    let tone: SparkBar['tone'] = 'day'
    if (!v) {
      tone = 'zero'
    } else if (i === days.length - 1) {
      tone = 'today'
    }
    return {
      day: d.day,
      readers: v,
      px: v ? Math.max(3, Math.round((v / max) * SPARK_MAX_PX)) : 2,
      tone,
    }
  })
}

export interface ReadersHead {
  /** "Readers · Oct so far" (the CSS uppercases it). */
  label: string
  figure: string
  /** "+40 today"; null on a day without readers yet. */
  today: string | null
}

export function readersHead(stats: Stats): ReadersHead {
  const last = stats.readers.days[stats.readers.days.length - 1]
  const today = sentCount(last?.readers) ?? 0
  return {
    label: `Readers · ${monthLabel(stats.month)} so far`,
    figure: fmtCount(sentCount(stats.readers.month_so_far) ?? 0),
    today: today > 0 ? `+${today.toLocaleString('en-US')} today` : null,
  }
}

export interface Tile {
  figure: string
  sub: string
}

/**
 * Reading time: the Stats tab's B.1 rule (`readingTimeState` decides the state) in the menu
 * tile's SHORT copy (coordinator ruling 2026-10-11, so the tile fits the reserved height):
 * "1m 12s" / "avg read · median 58s"; "Collecting" / "since Oct 8 · avg after 5 readers"; else
 * "—" / "reading time starts soon". The tab card keeps the full sentences.
 */
export function readingTile(stats: Stats): Tile {
  const state = readingTimeState(
    stats.reading_time,
    stats.community.median_read_seconds,
    OWN(stats.handle)
  )
  if (state.kind === 'figure') {
    return {
      figure: state.figure,
      sub: state.median ? `avg read · median ${state.median}` : 'avg read',
    }
  }
  if (state.kind === 'collecting') {
    // The state already proved the day readable; only its label is printed here.
    return {
      figure: state.figure,
      sub: `since ${dayLabel(stats.reading_time.collecting_since ?? null)} · avg after 5 readers`,
    }
  }
  return { figure: '—', sub: 'reading time starts soon' }
}

/** Rank: this month's when ranked ("#4" / "in October · of 13"), else all time ("#1" / "all time ·
 *  not ranked in Oct"), else "—" / "not ranked yet". */
export function rankTile(stats: Stats): Tile {
  const month = sentCount(stats.points.rank_month)
  if (month) {
    const of = sentCount(stats.points.ranked_total_month)
    return {
      figure: `#${month.toLocaleString('en-US')}`,
      sub: `in ${monthLabel(stats.month, 'long')}${
        of ? ` · of ${of.toLocaleString('en-US')}` : ''
      }`,
    }
  }
  const all = sentCount(stats.points.rank_all)
  if (all) {
    return {
      figure: `#${all.toLocaleString('en-US')}`,
      sub: `all time · not ranked in ${monthLabel(stats.month)}`,
    }
  }
  return { figure: '—', sub: 'not ranked yet' }
}

/** Reviews most-read first (DealHunt sorts; sorted again so an upstream reorder cannot shuffle). */
function byReaders(stats: Stats): StatsReview[] {
  return stats.reviews
    .slice()
    .sort((a, b) => (sentCount(b.views_total) ?? 0) - (sentCount(a.views_total) ?? 0))
}

/** The one fact worth checking in for: owners who reviewed the member's gear since they did, else
 *  how long the most-read review is read for; null when neither is known. */
export function factLine(stats: Stats): string | null {
  const after = stats.reviews.reduce(
    (n, r) => n + (sentCount(r.community?.owners_after_you) ?? 0),
    0
  )
  if (after > 0) {
    return after === 1
      ? '1 owner has reviewed your gear since you did.'
      : `${after.toLocaleString('en-US')} owners have reviewed your gear since you did.`
  }
  const [top] = byReaders(stats)
  const time = top ? fmtSeconds(top.avg_read_seconds_90d) : null
  return top && time ? `Your ${top.name} review is read for ${time} on average.` : null
}

function hasPhoto(review: StatsReview): boolean {
  return (sentCount(review.image_count) ?? 0) > 0 || review.photo_status === 'verified'
}

/** "+15 pts"; null without DealHunt's figure. */
function ptsPill(n: number | null): string | null {
  return n ? `+${n.toLocaleString('en-US')} pts` : null
}

export interface UpNext {
  label: string
  /** "+15 pts" — DealHunt's figure; null = no pill (a badge, or no table). */
  pts: string | null
}

/**
 * "Up next" (with data only — no review shows the fresh card): a review without a photo → add
 * one; no reply anywhere (`counts.replies_all` 0) → reply to another owner; else the Stats tab's
 * closest badge (`nextBadge`), no points. Unknown inputs skip their rule; null when none applies.
 */
export function upNext(
  answer: MenuStatsResponse,
  standing: CommunityStanding | null
): UpNext | null {
  const { stats, member, points, caps } = answer
  const bare = byReaders(stats).find(r => !hasPhoto(r))
  if (bare) {
    return {
      label: `Add a photo to your ${bare.name} review`,
      pts: ptsPill(points?.photo_verified ?? null),
    }
  }
  if (member && member.replies_all === 0) {
    return { label: 'Reply to another owner', pts: ptsPill(points?.reply ?? null) }
  }
  const badge = member ? nextBadge(member.badges, standing, caps) : null
  if (badge) {
    return { label: badge.line ? `${badge.name} · ${badge.line}` : badge.name, pts: null }
  }
  return null
}
